import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useShop } from 'shop/hooks'
import throttle from 'lodash/throttle'
import { CategoryWithProducts } from '../Shop/Categories/types'
import { CategoryGrouping } from './CategoryGrouping'
import { LoadingContainer } from './LoadingContainer'
import { EmptyState } from './EmptyState'
import { useShopPage } from 'shop/hooks'
import { ProductListMeta } from 'shop/components'
import {
  useVirtualizedCategories,
  InfiniteScrollSentinel
} from 'shop/hooks/useVirtualizedCategories'

interface Props {
  selectedCategory: string
  categories: CategoryWithProducts[] | null
  setActiveCategory: (key: string) => void
}

const ProductList = ({
  categories,
  selectedCategory,
  setActiveCategory
}: Props) => {
  const { isStoreLoading, isProductsLoading } = useShop()
  const {
    isAutoScrolling,
    searchValue,
    setSearchValue,
    shouldShowAllCategories
  } = useShopPage()
  const [categoryElements, setCategoryElements] = useState<{
    [key: string]: { top: number; bottom: number }
  }>({})

  // Store last known product count, default is 6 on first load
  const prevProductCount = useRef(6)

  // Show 3 initial categories, then load 2 more on scroll as scroll sentinel comes into viewport
  // When shouldShowAllCategories is true (when user clicks category in category bar), show all categories to allow scrolling to work
  const { visibleCategories, hasMore, sentinelRef } = useVirtualizedCategories(
    categories || [],
    3,
    2,
    shouldShowAllCategories
  )

  const pageWidth = window.innerWidth

  useEffect(() => {
    if (isStoreLoading || isProductsLoading || !visibleCategories.length) {
      return
    }

    const offsetY = window.pageYOffset

    const elementsUpdate = visibleCategories.reduce((acc, cur) => {
      const element = document.getElementById(
        `product-category-${cur.id || cur.name}`
      )

      if (!element) {
        return acc
      }

      return {
        ...acc,
        [cur.id || cur.name]: {
          top: element.getBoundingClientRect().top + offsetY,
          bottom: element.getBoundingClientRect().bottom + offsetY
        }
      }
    }, {})

    setCategoryElements(elementsUpdate)
  }, [isStoreLoading, isProductsLoading, visibleCategories, pageWidth])

  const handleSetActiveCategory = useCallback(
    (key: string) => {
      if (selectedCategory === key) {
        return
      }

      setActiveCategory(key)
    },
    [selectedCategory, setActiveCategory]
  )

  const handleScroll = useCallback(() => {
    if (isAutoScrolling) {
      return
    }

    const keys = Object.keys(categoryElements)
    const pageBottomOffset = 100

    if (
      window.scrollY + window.innerHeight >=
      document.documentElement.scrollHeight - pageBottomOffset
    ) {
      handleSetActiveCategory(keys[keys.length - 1])

      return
    }

    const pageViewPointOffset = 200

    keys.forEach((key) => {
      if (!categoryElements[key]) {
        return
      }

      const upperBound = categoryElements[key].bottom
      const lowerBound = categoryElements[key].top

      if (
        window.scrollY + pageViewPointOffset <= upperBound &&
        window.scrollY + pageViewPointOffset >= lowerBound
      ) {
        handleSetActiveCategory(key)
      }
    })
  }, [categoryElements, handleSetActiveCategory, isAutoScrolling])

  useEffect(() => {
    const onScroll = throttle(handleScroll, 300)

    document.addEventListener('scroll', onScroll)

    return () => {
      document.removeEventListener('scroll', onScroll)
    }
  }, [handleScroll])

  // Memoize the product count - recalculates when categories change
  const visibleProductCount = useMemo(() => {
    if (!categories?.length) {
      // Return last known count when categories is undefined
      return prevProductCount.current
    }

    const currentProductCount = categories.reduce(
      (total, cat) => total + cat.products.length,
      0
    )
    // Update ref with new count
    prevProductCount.current = currentProductCount
    return currentProductCount
  }, [categories])

  if (isStoreLoading || isProductsLoading || !categories) {
    return <LoadingContainer count={visibleProductCount} />
  }

  if (
    categories &&
    (!categories.length ||
      (categories.length === 1 && !categories[0].products.length))
  ) {
    return (
      <EmptyState searchValue={searchValue} setSearchValue={setSearchValue} />
    )
  }

  return (
    <>
      <ProductListMeta
        products={categories.flatMap((category) => category.products)}
      />
      <CategoryGrouping visibleCategories={visibleCategories} />
      <InfiniteScrollSentinel sentinelRef={sentinelRef} hasMore={hasMore} />
    </>
  )
}

export default ProductList
